import { useEffect, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { AlertTriangle, FileStack, Info } from 'lucide-react';
import { ManualSystemPicker } from '../../components/ManualSystemPicker';
import { ProjectDetailSection } from '../../components/project-detail/ProjectDetailSection';
import { formatTechnischeIdVoorWeergave } from '../../lib/drawing-analysis-display';
import type { ApproveDrawingProposalMode } from '../../types/project';
import type { ProjectDetailOutletContext } from './outletTypes';

const proposalFieldClass =
  'w-full min-w-0 rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

const APPROVE_MODE_OPTIONS: Array<{
  value: ApproveDrawingProposalMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'append',
    label: 'Toevoegen',
    hint: 'Huidige regels blijven staan; het voorstel komt eronder (nieuwe regels).',
  },
  {
    value: 'replace',
    label: 'Vervangen',
    hint: 'Hele begroting wordt het voorstel; opgeslagen regels gaan verloren.',
  },
  {
    value: 'merge',
    label: 'Samenvoegen bij dubbele regels',
    hint: 'Zelfde systeem + variant + bouwcluster/verdieping/ruimte → aantallen worden opgeteld; anders als nieuwe regel toegevoegd.',
  },
];

export function PostenTab() {
  const v = useOutletContext<ProjectDetailOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [approveProposalMode, setApproveProposalMode] = useState<ApproveDrawingProposalMode>('append');
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const focusLineId = searchParams.get('line')?.trim() || '';

  useEffect(() => {
    if (v.pendingDrawingApproval && v.savedSystemsCount > 0) {
      setApproveProposalMode('append');
    }
  }, [v.proj.drawing_proposal?.audit_id, v.pendingDrawingApproval, v.savedSystemsCount]);

  useEffect(() => {
    if (!focusLineId) return;
    let cancelled = false;
    const timeouts: number[] = [];
    const clearLineParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('line');
          return next;
        },
        { replace: true }
      );
    };
    const tryScroll = (attempt: number) => {
      if (cancelled) return;
      const el = document.getElementById(`boq-line-${focusLineId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
        timeouts.push(
          window.setTimeout(() => {
            if (!cancelled) el.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
          }, 2200)
        );
        clearLineParam();
        return;
      }
      if (attempt >= 10) {
        clearLineParam();
        return;
      }
      timeouts.push(window.setTimeout(() => tryScroll(attempt + 1), 100));
    };
    tryScroll(0);
    return () => {
      cancelled = true;
      for (const t of timeouts) window.clearTimeout(t);
    };
  }, [focusLineId, setSearchParams]);

  useEffect(() => {
    if (!approveConfirmOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setApproveConfirmOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [approveConfirmOpen]);

  const effectiveApproveMode: ApproveDrawingProposalMode =
    v.savedSystemsCount > 0 ? approveProposalMode : 'replace';
  const selectedModeLabel =
    APPROVE_MODE_OPTIONS.find((o) => o.value === effectiveApproveMode)?.label ?? effectiveApproveMode;
  const proposalCount = v.proposalRows?.length ?? 0;

  return (
    <>
      {v.pendingDrawingApproval && v.proposalRows && v.proj.drawing_proposal && (
        <ProjectDetailSection
          id="tekening-voorstel"
          icon={AlertTriangle}
          variant="warning"
          title="Voorstel uit tekeninganalyse"
          description={
            <>
              Controleer metingen en prijsboek-koppelingen; pas hoeveelheden aan. Op Gegevens kun je posten in{' '}
              <strong className="text-text">meerdere keren</strong> uit dezelfde analyse importeren — dit blok verschijnt
              zodra het volledige concept klaar is om goed te keuren. Kies hieronder hoe dit voorstel met je{' '}
              <strong className="text-text">opgeslagen</strong> posten moet worden gecombineerd.{' '}
              <Link to="/help/tekening" className="font-medium text-primary hover:underline">
                Uitleg Tekening &amp; PDF
              </Link>
            </>
          }
        >
          {v.savedSystemsCount > 0 && (
            <div className="mb-4 rounded-lg border border-amber-300/80 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              <strong className="text-text">Let op:</strong> dit dossier heeft al opgeslagen begrotingsregels. Kies vóór
              goedkeuren wat er moet gebeuren — standaard staat <strong className="text-text">toevoegen</strong> (minst
              destructief).
            </div>
          )}
          {v.proj.drawing_proposal.analysis_summary ? (
            <div className="mb-5 rounded-lg border border-border/80 bg-surface/90 px-4 py-3">
              <h3 className="mb-2 text-sm font-semibold text-text">Samenvatting van de analyse</h3>
              <p className="text-sm leading-relaxed text-text-muted">{v.proj.drawing_proposal.analysis_summary}</p>
            </div>
          ) : null}
          {v.savedSystemsCount > 0 && (
            <fieldset className="mb-5 space-y-3 rounded-lg border border-border/70 bg-surface-card px-3 py-3">
              <legend className="px-1 text-sm font-semibold text-text">Hoe het voorstel toepassen</legend>
              {APPROVE_MODE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex cursor-pointer gap-3 rounded-md border border-transparent px-1 py-2 hover:bg-surface/80 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary"
                >
                  <input
                    type="radio"
                    name="approve-drawing-mode"
                    value={opt.value}
                    checked={approveProposalMode === opt.value}
                    onChange={() => setApproveProposalMode(opt.value)}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="block font-medium text-text">{opt.label}</span>
                    <span className="mt-0.5 block text-xs text-text-muted">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
          <h3 className="mb-2 text-sm font-semibold text-text">Voorgestelde regels (controleer en pas aan)</h3>
          <ul className="mb-5 space-y-3">
            {v.proposalRows.map((row, i) => (
              <li
                key={row.line_id ?? `${row.system_id}-${row.variant_id}-${i}`}
                className="rounded-lg border border-border/70 bg-surface-card p-3 text-sm shadow-sm"
              >
                <p className="mb-2 font-medium text-text">
                  {formatTechnischeIdVoorWeergave(row.system_id)}
                  <span className="font-normal text-text-muted"> · </span>
                  <span className="text-text-muted">{formatTechnischeIdVoorWeergave(row.variant_id)}</span>
                </p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="block sm:col-span-1">
                    <span className="mb-0.5 block text-xs font-medium text-text-muted">Aantal</span>
                    <input
                      type="number"
                      min={0.01}
                      step={0.01}
                      value={row.quantity}
                      onChange={(e) => {
                        const q = Number(e.target.value) || 1;
                        v.setProposalRows((prev) =>
                          prev ? prev.map((r, j) => (j === i ? { ...r, quantity: q } : r)) : prev
                        );
                      }}
                      className={`${proposalFieldClass} font-mono`}
                    />
                  </label>
                  <label className="block sm:col-span-1 lg:col-span-1">
                    <span className="mb-0.5 block text-xs font-medium text-text-muted">Bouwcluster</span>
                    <input
                      type="text"
                      value={row.building_part ?? ''}
                      placeholder="bijv. woning, garage"
                      onChange={(e) => {
                        const t = e.target.value;
                        v.setProposalRows((prev) =>
                          prev
                            ? prev.map((r, j) =>
                                j === i ? { ...r, building_part: t || undefined } : r
                              )
                            : prev
                        );
                      }}
                      className={proposalFieldClass}
                    />
                  </label>
                  <label className="block sm:col-span-1">
                    <span className="mb-0.5 block text-xs font-medium text-text-muted">Verdieping</span>
                    <input
                      type="text"
                      value={row.floor ?? ''}
                      placeholder="bijv. begane grond"
                      onChange={(e) => {
                        const t = e.target.value;
                        v.setProposalRows((prev) =>
                          prev ? prev.map((r, j) => (j === i ? { ...r, floor: t || undefined } : r)) : prev
                        );
                      }}
                      className={proposalFieldClass}
                    />
                  </label>
                  <label className="block sm:col-span-1">
                    <span className="mb-0.5 block text-xs font-medium text-text-muted">Ruimte / nummer</span>
                    <input
                      type="text"
                      value={row.room_number ?? ''}
                      placeholder="bijv. keuken, 1.04"
                      onChange={(e) => {
                        const t = e.target.value;
                        v.setProposalRows((prev) =>
                          prev
                            ? prev.map((r, j) =>
                                j === i ? { ...r, room_number: t || undefined } : r
                              )
                            : prev
                        );
                      }}
                      className={proposalFieldClass}
                    />
                  </label>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <button
              type="button"
              onClick={() => setApproveConfirmOpen(true)}
              disabled={v.approving}
              className={v.btnPrimaryLg}
            >
              {v.approving ? 'Bezig…' : 'Metingen goedkeuren en invoegen'}
            </button>
            <button
              type="button"
              onClick={() => void v.handleClearDrawingProposal()}
              disabled={v.approving}
              className={`${v.btnSecondary} disabled:opacity-50`}
            >
              Voorstel wissen
            </button>
          </div>
        </ProjectDetailSection>
      )}

      {approveConfirmOpen && v.pendingDrawingApproval && v.proposalRows && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="approve-drawing-modal-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
            aria-label="Sluit dialoog"
            onClick={() => setApproveConfirmOpen(false)}
          />
          <div className="relative z-10 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border border-border bg-surface-card p-5 shadow-xl">
            <h3 id="approve-drawing-modal-title" className="text-lg font-semibold text-text">
              Goedkeuren bevestigen
            </h3>
            <p className="mt-2 text-sm text-text-muted">
              Je staat op het punt <strong className="text-text">{proposalCount}</strong>{' '}
              {proposalCount === 1 ? 'voorgestelde regel' : 'voorgestelde regels'} definitief in dit dossier te zetten.
            </p>
            {v.savedSystemsCount > 0 ? (
              <>
                <p className="mt-3 text-sm text-text">
                  Gekozen actie: <strong className="text-text">{selectedModeLabel}</strong>
                </p>
                <p className="mt-1 text-xs text-text-muted">
                  {APPROVE_MODE_OPTIONS.find((o) => o.value === effectiveApproveMode)?.hint}
                </p>
                {effectiveApproveMode === 'replace' && (
                  <div
                    className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/50 dark:text-red-100"
                    role="alert"
                  >
                    <strong className="text-text">Let op:</strong> bij <strong className="text-text">Vervangen</strong>{' '}
                    gaan je huidige <strong className="text-text">{v.savedSystemsCount}</strong> opgeslagen
                    begrotingsregels verloren.
                  </div>
                )}
              </>
            ) : (
              <p className="mt-3 text-sm text-text-muted">
                Er staan nog geen opgeslagen posten: je begroting wordt dit voorstel.
              </p>
            )}
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                className={v.btnSecondary}
                disabled={v.approving}
                onClick={() => setApproveConfirmOpen(false)}
              >
                Annuleren
              </button>
              <button
                type="button"
                className={v.btnPrimary}
                disabled={v.approving}
                onClick={() => {
                  setApproveConfirmOpen(false);
                  void v.handleApproveDrawingProposal(effectiveApproveMode);
                }}
              >
                Ja, goedkeuren
              </button>
            </div>
          </div>
        </div>
      )}

      {!v.pendingDrawingApproval && (
        <>
          {(() => {
            const prof = v.customerUxProfile;
            const n = v.boqDraft.length;
            const noCluster = v.boqDraft.filter((r) => !(r.building_part ?? '').trim()).length;
            const showHint =
              prof.emphasizeClusterInPosten && n >= 2 && noCluster / n >= 0.5;
            if (!showHint) return null;
            return (
              <div className="mb-4 flex gap-3 rounded-lg border border-sky-200/90 bg-sky-50/90 px-4 py-3 text-sm text-sky-950 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-100">
                <Info className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                <p>
                  <strong className="text-text">Tip voor jouw klanttype:</strong> meer dan de helft van je regels heeft
                  nog geen <strong className="text-text">bouwcluster</strong>. Vul die in voor duidelijke groepering in
                  Resultaat en rapportages (bijv. per appartement of verdieping).
                </p>
              </div>
            );
          })()}
          <ProjectDetailSection
            id="begrotingsposten"
            icon={FileStack}
            title="Begrotingsregels"
            description="Stap 1: prijsboek doorzoeken en bouwdeel kiezen. Stap 2: variant, aantal en bouwcluster. Stap 3: regels finetunen, ordenen (pijltjes), undo/redo — daarna opslaan om Resultaat te ontgrendelen."
          >
            <ManualSystemPicker
              value={v.boqDraft}
              onChange={v.setBoqDraft}
              projectId={v.projectId}
              catalogProjectType={v.projectType}
              catalogBuildingType={v.buildingType}
              tenantId={v.proj.tenant_id ?? 'default'}
              canUndo={v.boqCanUndo}
              canRedo={v.boqCanRedo}
              onUndo={v.boqUndo}
              onRedo={v.boqRedo}
            />
            <button
              type="button"
              onClick={() => void v.handleSaveBoq()}
              disabled={v.boqSaving || v.boqDraft.length === 0}
              className={`mt-5 ${v.btnPrimary}`}
            >
              {v.boqSaving ? 'Opslaan…' : 'Regels opslaan'}
            </button>
          </ProjectDetailSection>
        </>
      )}

      {!v.hasSystems && !v.pendingDrawingApproval && (
        <div className="mb-6 flex flex-col items-center gap-4 rounded-xl border border-dashed border-border bg-surface-card/60 py-10">
          <img
            src="/assets/empty-results.png"
            alt="Illustratie: nog geen begrotingsregels — voeg posten toe via Gegevens of het prijsboek"
            className="max-h-36 w-auto object-contain opacity-90"
            width={300}
            height={200}
            decoding="async"
          />
          <p className="max-w-md text-center text-sm text-text-muted">
            Nog geen posten. Op <strong className="text-text">Gegevens</strong> kun je een tekening of PDF uploaden of
            documenten uit de documentenbibliotheek aanvinken, analyseren en posten{' '}
            <strong className="text-text">in delen</strong> importeren — of hieronder handmatig regels uit het prijsboek
            toevoegen.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
            <Link to={`/projects/${v.projectId}/gegevens`} className="font-medium text-primary hover:underline">
              ← Gegevens &amp; tekeninganalyse
            </Link>
            <span className="text-text-muted" aria-hidden>
              ·
            </span>
            <Link to="/help/tekening" className="font-medium text-primary hover:underline">
              Help: Tekening &amp; PDF
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
